import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getShareLinkData } from '../../utils/shareLinkService';
import { getStreetAddressOnly } from '../../utils/addressParser';
import Icon from '../../components/AppIcon';
import NotificationToast from '../../components/ui/NotificationToast';
import ConcalLogo from '../../components/ConcalLogo';
import UsefulLinks from '../../components/UsefulLinks';

const SharePage = () => {
  const { shareId } = useParams();
  const [deal, setDeal] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [additionalTerms, setAdditionalTerms] = useState([]);
  const [partyInfo, setPartyInfo] = useState({});
  const [parties, setParties] = useState([]);
  const [uploadedContract, setUploadedContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    loadSharedData();
  }, [shareId]);

  const loadSharedData = async () => {
    try {
      if (!shareId) {
        throw new Error('Invalid share link');
      }
      const data = await getShareLinkData(shareId);
      console.log('🔥 SharePage - Loaded data from getShareLinkData:', data);
      console.log('🔥 SharePage - Parties data:', data.parties);
      setDeal(data.deal);
      setMilestones(data.milestones);
      setDeposits(data.deposits);
      setAdditionalTerms(data.additionalTerms);
      setPartyInfo(data.partyInfo);
      setParties(data.parties || []);
      setUploadedContract(data.uploadedContract || null);
      console.log('🔥 SharePage - State set, parties:', data.parties);
    } catch (err) {
      console.error('Error loading shared data:', err);
      setError(err.message);
      addNotification({
        type: 'error',
        title: 'Load Error',
        message: err.message || 'Failed to load shared contract data'
      });
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { ...notification, id }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, notification.duration || 5000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  const handleAddToCalendar = (milestone) => {
    if (!milestone.start_dt) return;

    // Format title with street address only (no city/state/zip)
    const streetAddress = getStreetAddressOnly(deal?.property_street) || 'Property';
    const eventTitle = `${streetAddress} - ${milestone.title}`;
    try {
      const eventDate = new Date(milestone.start_dt);
      const startDate = eventDate.toISOString().split('T')[0].replace(/-/g, '');
      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: eventTitle,
        dates: `${startDate}/${startDate}`,
        details: `Contract deadline: ${milestone.title}`,
        location: deal?.property_street ? `${deal.property_street}, ${deal.city}` : '',
        ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      const calendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;

      // On mobile, use same window to avoid blank page issue
      if (isMobileDevice()) {
        window.location.href = calendarUrl;
      } else {
        // On desktop, open in new tab
        window.open(calendarUrl, '_blank');
      }

      addNotification({
        type: 'success',
        title: 'Calendar Link Opened',
        message: 'Google Calendar opened'
      });
    } catch (err) {
      console.error('Error creating calendar link:', err);
      addNotification({
        type: 'error',
        title: 'Calendar Error',
        message: 'Unable to create calendar event'
      });
    }
  };

  const handleDocumentDownload = (contract) => {
    try {
      if (!contract.url) {
        throw new Error('No document URL available');
      }

      // Create a link element for download
      const link = document.createElement('a');
      link.href = contract.url;
      link.download = contract.name || 'contract.pdf';

      // Append to body temporarily
      document.body.appendChild(link);

      // Trigger click
      link.click();

      // Remove after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);

      addNotification({
        type: 'success',
        title: 'Downloaded',
        message: `${contract.name} downloaded successfully`
      });
    } catch (err) {
      console.error('Error downloading document:', err);
      addNotification({
        type: 'error',
        title: 'Download Error',
        message: 'Unable to download document'
      });
    }
  };

  const handleDownloadICS = (milestone) => {
    if (!milestone.start_dt) return;

    try {
      const eventDate = new Date(milestone.start_dt);
      const startDate = eventDate.toISOString().split('T')[0].replace(/-/g, '');
      const endDate = startDate;

      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Concal//Contract Dates//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${milestone.id || `milestone-${Date.now()}`}
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}Z
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${(getStreetAddressOnly(deal?.property_street) || 'Property')} - ${milestone.title}
DESCRIPTION:Contract deadline: ${milestone.title}
LOCATION:${deal?.property_street ? `${deal.property_street}, ${deal.city}` : ''}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${milestone.title.replace(/\s+/g, '_')}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Delay revoking the blob URL to ensure download completes on mobile
      // Mobile browsers (especially Safari) need more time to process the download
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);

      addNotification({
        type: 'success',
        title: 'Downloaded',
        message: `${milestone.title} calendar file downloaded`
      });
    } catch (err) {
      console.error('Error downloading ICS file:', err);
      addNotification({
        type: 'error',
        title: 'Download Error',
        message: 'Unable to download calendar file'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1d2e]">
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1d2e]/80 backdrop-blur-md border-b border-gray-700/50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <ConcalLogo size="large" className="w-auto h-10" />
              </div>
              <div className="text-sm text-gray-400">Shared Contract Details</div>
            </div>
          </div>
        </header>

        <main className="pt-24 pb-12">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="Loader" className="animate-spin mx-auto mb-4" size={48} />
              <p className="text-gray-300">Loading contract details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="min-h-screen bg-[#1a1d2e]">
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1d2e]/80 backdrop-blur-md border-b border-gray-700/50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <ConcalLogo size="large" className="w-auto h-10" />
              </div>
              <div className="text-sm text-gray-400">Shared Contract Details</div>
            </div>
          </div>
        </header>

        <main className="pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white rounded-lg p-8 text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-red-500" />
              <h1 className="text-2xl font-bold text-black mb-2">Link Not Found</h1>
              <p className="text-gray-600">{error || 'This share link has expired or is invalid.'}</p>
              <a href="/" className="mt-6 inline-block bg-black text-white px-6 py-2 rounded hover:bg-gray-800 transition-colors">
                Return to Home
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1d2e]">
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1d2e]/80 backdrop-blur-md border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <ConcalLogo size="large" className="w-auto h-10" />
            </div>
            <div className="text-sm text-gray-400">Shared Contract Details</div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Page Header with Logo */}
          <div className="text-center mb-8">
            {/* Agent Logo */}
            {deal?.logo_url && (
              <div className="flex items-center justify-center mb-6">
                <div className="bg-white rounded-lg p-4 shadow-lg">
                  <img
                    src={deal.logo_url}
                    alt="Agent Logo"
                    style={{
                      maxWidth: '150px',
                      maxHeight: '80px',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                    className="rounded"
                  />
                </div>
              </div>
            )}

            {/* Congratulatory Heading - Brand Design */}
            <div className="mb-12">
              {/* "Congratulations" in Playfair Display italic font */}
              <h1
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(48px, 12vw, 80px)',
                  color: '#bb5d10',
                  marginBottom: '4px',
                  letterSpacing: '0.02em',
                  lineHeight: '1.2',
                  fontStyle: 'italic',
                  fontWeight: '400'
                }}
              >
                Congratulations
              </h1>

              {/* Property Address + "is under agreement" in Montserrat font */}
              <p
                style={{
                  fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontSize: 'clamp(24px, 6vw, 40px)',
                  color: '#eeebe3',
                  fontWeight: '500',
                  marginBottom: '24px',
                  letterSpacing: '0.01em',
                  lineHeight: '1.4'
                }}
              >
                {deal?.property_street
                  ? `${getStreetAddressOnly(deal.property_street)}`
                  : 'Your Property'}{' '}
                is under agreement
              </p>
            </div>

            {/* Subheading - Optional, moved lower */}
            <p
              style={{
                fontSize: '14px',
                color: '#9ca3af',
                maxWidth: '42rem',
                lineHeight: '1.6',
                marginBottom: '32px'
              }}
            >
              Below are important dates and terms you can add to your calendar.
            </p>
          </div>

          {/* Deal Information Card */}
          <div className="bg-white rounded-lg p-6 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              {deal?.buyer_names && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Buyer</p>
                  <p className="text-sm font-medium text-black">{deal.buyer_names}</p>
                </div>
              )}
              {deal?.seller_names && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Seller</p>
                  <p className="text-sm font-medium text-black">{deal.seller_names}</p>
                </div>
              )}
              {deal?.property_street && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Property Address</p>
                  <p className="text-sm font-medium text-black">
                    {deal.property_street}
                    {deal.city && `, ${deal.city}`}
                    {deal.state && ` ${deal.state}`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Parties Section */}
          {(deal?.buyer_names || deal?.seller_names || (parties && parties.length > 0)) && (
            <div className="bg-white rounded-lg mb-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2
                  style={{
                    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#bb5d10'
                  }}
                >
                  Parties Involved
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                {/* Buyer */}
                {deal?.buyer_names && (
                  <div className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                      Buyer
                    </div>
                    <h3 className="text-lg font-semibold text-black">{deal.buyer_names}</h3>
                  </div>
                )}

                {/* Seller */}
                {deal?.seller_names && (
                  <div className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                      Seller
                    </div>
                    <h3 className="text-lg font-semibold text-black">{deal.seller_names}</h3>
                  </div>
                )}

                {/* Additional Parties */}
                {parties && Array.isArray(parties) && parties.map((party) => (
                  <div key={party.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="inline-block bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                      {party.type ? party.type.charAt(0).toUpperCase() + party.type.slice(1) : 'Party'}
                    </div>
                    <h3 className="text-lg font-semibold text-black">{party.name}</h3>
                    <div className="mt-2 space-y-1">
                      {party.email && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Icon name="Mail" size={14} />
                          <a
                            href={`mailto:${party.email}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {party.email}
                          </a>
                        </div>
                      )}
                      {party.phone && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Icon name="Phone" size={14} />
                          <a
                            href={`tel:${party.phone}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {party.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dates Section */}
          {milestones.length > 0 && (
            <div className="bg-white rounded-lg mb-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2
                  style={{
                    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#bb5d10'
                  }}
                >
                  Important Dates
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                {milestones.map((milestone) => (
                  <div key={milestone.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-black mb-2">{milestone.title}</h3>
                        <div className="flex items-center space-x-4 text-gray-600 flex-wrap gap-2">
                          <div className="flex items-center space-x-2">
                            <Icon name="Calendar" size={16} />
                            <span className="text-sm">
                              {formatDate(milestone.start_dt)}
                            </span>
                          </div>
                          {!milestone.all_day && milestone.start_dt && (
                            <div className="flex items-center space-x-2">
                              <Icon name="Clock" size={16} />
                              <span className="text-sm">
                                {formatTime(milestone.start_dt)}
                              </span>
                            </div>
                          )}
                          {milestone.all_day && (
                            <span className="text-sm text-gray-500">All Day</span>
                          )}
                        </div>
                        {milestone.description && (
                          <p className="text-sm text-gray-600 mt-2">{milestone.description}</p>
                        )}
                      </div>

                      {/* Calendar Integration */}
                      {milestone.start_dt && (
                        <div className="flex gap-2 flex-shrink-0">
                          <div className="relative group">
                            <button
                              onClick={() => handleAddToCalendar(milestone)}
                              className="p-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
                              title="Add to Google Calendar"
                            >
                              <Icon name="Calendar" size={16} />
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              Add to Calendar
                              <div className="absolute top-full right-2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>

                          <div className="relative group">
                            <button
                              onClick={() => handleDownloadICS(milestone)}
                              className="p-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
                              title="Download .ics file"
                            >
                              <Icon name="Download" size={16} />
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              Download .ics
                              <div className="absolute top-full right-2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {milestones.length === 0 && (
            <div className="bg-white rounded-lg p-8 text-center mb-4">
              <Icon name="AlertCircle" size={32} className="mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600">No dates scheduled for this contract.</p>
            </div>
          )}

          {/* Deposits Due Section */}
          {deposits.length > 0 && (
            <div className="bg-white rounded-lg mb-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2
                  style={{
                    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#bb5d10'
                  }}
                >
                  Deposits Due
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                {deposits.map((deposit) => (
                  <div key={deposit.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-black">{deposit.description}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Terms Section */}
          {additionalTerms.length > 0 && (
            <div className="bg-white rounded-lg mb-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2
                  style={{
                    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#bb5d10'
                  }}
                >
                  Additional Terms
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                {additionalTerms.map((term) => (
                  <div key={term.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <p className="text-black font-medium">{term.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents Section */}
          {uploadedContract && (
            <div className="bg-white rounded-lg mb-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2
                  style={{
                    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#bb5d10',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icon name="FileText" size={20} />
                  <span>Documents</span>
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                <div className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 flex items-center space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                        <Icon name="FileText" size={16} className="text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-black font-medium truncate">{uploadedContract.name}</p>
                        <p className="text-sm text-gray-600">
                          {uploadedContract.size ? `${(uploadedContract.size / 1024 / 1024).toFixed(2)} MB` : 'Original Contract'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDocumentDownload(uploadedContract)}
                      className="ml-4 px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors text-sm font-medium flex items-center space-x-2 flex-shrink-0"
                    >
                      <Icon name="Download" size={16} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-6 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              Powered by <span className="font-semibold text-white">concal</span>
            </p>
          </div>
        </div>

        {/* Useful Links Section - Mobile Collapsible */}
        <div className="max-w-4xl mx-auto px-6 mt-12">
          <UsefulLinks
            propertyCity={deal?.city || 'Your Location'}
            isReadOnly={true}
            isMobile={true}
            dealId={deal?.id}
          />
        </div>
      </main>

      {/* Notifications */}
      {notifications.length > 0 && (
        <NotificationToast
          notifications={notifications}
          onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
          maxVisible={3}
        />
      )}
    </div>
  );
};

export default SharePage;
